// src/prototype/data/adapters/enterpriseSpineAdapter.ts
// ============================================================
// ENTERPRISE PROTOTYPE — OVER-TIME SPINE · ADAPTER (the only mapping seam)
//
// Reads BigQuery-shaped rows (from ./enterpriseSpineMock — a live fetch later) and returns
// view models. Consumers get view models, never raw rows.
//
// FUNDING LINE (Phase 1.1): NOT a hardcoded top-N. Walk the run in rank order, accumulate
// capital_ask, and fund down the list until the run's capital cap is exhausted — a run
// with no scenario uses BASELINE_BUDGET_CAP ($50M); a scenario run uses its budget_cap
// ($40M for Q2). The cap therefore determines the line, and a budget cut (or a big-ticket
// ask near the line) moves the line up even when ranks don't change.
// ============================================================

import {
  type ScoringRunVM,
  type ScenarioVM,
  type DecisionLogEntryVM,
  type DecisionLogFilters,
  type PortfolioAsOfVM,
  type PortfolioInitiativeVM,
  type PortfolioRollupVM,
  type ChangeDeltaVM,
  type InitiativeDelta,
  type StatusDelta,
  type DimensionScores,
  type RunScore,
  type Owner,
} from './enterpriseSpineTypes';
import {
  SCORING_RUNS,
  RUN_SCORES,
  SCENARIOS,
  DECISION_LOG,
  INITIATIVE_TITLES,
  INITIATIVE_OWNERS,
  INITIATIVE_CATEGORIES,
  CAPITAL_ASKS,
  BASELINE_BUDGET_CAP,
} from './enterpriseSpineMock';

const titleFor = (use_case_id: number): string => INITIATIVE_TITLES[use_case_id] ?? `Use case ${use_case_id}`;
const ownerFor = (use_case_id: number): Owner => INITIATIVE_OWNERS[use_case_id] ?? { name: 'Unassigned', org: 'Unassigned' };
const categoryFor = (use_case_id: number): string => INITIATIVE_CATEGORIES[use_case_id] ?? 'Uncategorized';
const runLabel = (run_id: string): string | undefined => SCORING_RUNS.find((r) => r.run_id === run_id)?.label;
const scenarioName = (scenario_id?: string): string | undefined =>
  scenario_id ? SCENARIOS.find((s) => s.scenario_id === scenario_id)?.name : undefined;
const scoresForRun = (run_id: string): RunScore[] => RUN_SCORES.filter((s) => s.run_id === run_id);
const round1 = (v: number) => Math.round(v * 10) / 10;
const round2 = (v: number) => Math.round(v * 100) / 100;

/** The capital cap in force for a run: its scenario's budget_cap, else the baseline. */
function capForRun(run_id: string): number {
  const run = SCORING_RUNS.find((r) => r.run_id === run_id);
  const scenario = run?.scenario_id ? SCENARIOS.find((s) => s.scenario_id === run.scenario_id) : undefined;
  return scenario ? scenario.levers.budget_cap : BASELINE_BUDGET_CAP;
}

/**
 * The funded set for a run: walk rank order, accumulate capital_ask, fund until the run's
 * cap can no longer absorb the next initiative (the funding line). Returns the funded ids
 * and the total committed capital.
 */
function fundedForRun(run_id: string): { ids: Set<number>; committed: number; cap: number } {
  const cap = capForRun(run_id);
  const ids = new Set<number>();
  let committed = 0;
  for (const r of scoresForRun(run_id).slice().sort((a, b) => a.rank - b.rank)) {
    const ask = CAPITAL_ASKS[r.use_case_id] ?? 0;
    if (committed + ask <= cap) {
      committed = round1(committed + ask);
      ids.add(r.use_case_id);
    } else {
      break; // funding line reached
    }
  }
  return { ids, committed, cap };
}

/** The capital cap ($M) in force for a run (its scenario's budget_cap, else the baseline). */
export function getRunCap(run_id: string): number {
  return capForRun(run_id);
}

/**
 * Per-initiative capital_ask ($M), keyed by use_case_id. Read-only surfacing of existing spine
 * data — the Scenarios surface walks this cumulatively against the cap to place the funding
 * line client-side as levers move (no spine write). Mirrors the adapter's own funding math.
 */
export function getCapitalAsks(): Record<number, number> {
  return { ...CAPITAL_ASKS };
}

/** All scoring runs, oldest → newest. */
export function getRuns(): ScoringRunVM[] {
  return [...SCORING_RUNS]
    .sort((a, b) => a.created_at.localeCompare(b.created_at))
    .map((r) => ({
      run_id: r.run_id,
      label: r.label,
      created_at: r.created_at,
      scenario_id: r.scenario_id,
      scenario_name: scenarioName(r.scenario_id),
      initiative_count: scoresForRun(r.run_id).length,
    }));
}

/** All saved scenarios. */
export function getScenarios(): ScenarioVM[] {
  return SCENARIOS.map((s) => ({
    scenario_id: s.scenario_id,
    name: s.name,
    base_run_id: s.base_run_id,
    base_run_label: runLabel(s.base_run_id),
    levers: s.levers,
    created_at: s.created_at,
  }));
}

/** Attributed decision log, newest → oldest, optionally filtered. */
export function getDecisionLog(filters: DecisionLogFilters = {}): DecisionLogEntryVM[] {
  return DECISION_LOG.filter(
    (d) =>
      (filters.use_case_id === undefined || d.use_case_id === filters.use_case_id) &&
      (filters.decision === undefined || d.decision === filters.decision) &&
      (filters.run_id === undefined || d.run_id === filters.run_id),
  )
    .slice()
    .sort((a, b) => b.decided_at.localeCompare(a.decided_at))
    .map((d) => ({
      decision_id: d.decision_id,
      use_case_id: d.use_case_id,
      title: titleFor(d.use_case_id),
      decision: d.decision,
      rationale: d.rationale,
      decided_by: d.decided_by,
      decided_at: d.decided_at,
      run_id: d.run_id,
      scenario_id: d.scenario_id,
      status_before: d.status_before,
      status_after: d.status_after,
    }));
}

/** The portfolio as it stood at a given run (funded line = cumulative capital vs cap). */
export function getPortfolioAsOf(run_id: string): PortfolioAsOfVM | null {
  const run = getRuns().find((r) => r.run_id === run_id);
  if (!run) return null;

  const funded = fundedForRun(run_id);
  const initiatives: PortfolioInitiativeVM[] = scoresForRun(run_id)
    .slice()
    .sort((a, b) => a.rank - b.rank)
    .map((s) => ({
      use_case_id: s.use_case_id,
      title: titleFor(s.use_case_id),
      rank: s.rank,
      composite: s.composite,
      confidence: s.confidence,
      dimension_scores: s.dimension_scores,
      owner: ownerFor(s.use_case_id),
      category: categoryFor(s.use_case_id),
      funded: funded.ids.has(s.use_case_id),
    }));

  const n = initiatives.length || 1;
  const dimAvg = (k: keyof DimensionScores) => round1(initiatives.reduce((acc, i) => acc + i.dimension_scores[k], 0) / n);
  const rollup: PortfolioRollupVM = {
    total_initiatives: initiatives.length,
    funded_count: funded.ids.size,
    avg_composite: round1(initiatives.reduce((acc, i) => acc + i.composite, 0) / n),
    avg_confidence: round2(initiatives.reduce((acc, i) => acc + i.confidence, 0) / n),
    dimension_averages: {
      financial_impact: dimAvg('financial_impact'),
      operational_urgency: dimAvg('operational_urgency'),
      strategic_alignment: dimAvg('strategic_alignment'),
      implementation_feasibility: dimAvg('implementation_feasibility'),
    },
  };

  // funding_line_top_n = the rank at which the funded line sits (i.e. the funded count).
  return { run, funding_line_top_n: funded.ids.size, initiatives, rollup };
}

/**
 * DERIVED per-initiative deltas between two runs (never stored).
 * rank_delta = from_rank − to_rank (positive ⇒ improved). score_delta = to − from composite.
 * status_delta compares funded-set membership (which is cap-driven), so an initiative can
 * DROP below the line with an unchanged rank purely because the budget shrank.
 */
export function getChangeDelta(from_run_id: string, to_run_id: string): ChangeDeltaVM | null {
  if (!SCORING_RUNS.some((r) => r.run_id === from_run_id)) return null;
  if (!SCORING_RUNS.some((r) => r.run_id === to_run_id)) return null;

  const fromById = new Map(scoresForRun(from_run_id).map((s) => [s.use_case_id, s]));
  const toById = new Map(scoresForRun(to_run_id).map((s) => [s.use_case_id, s]));
  const fromFunded = fundedForRun(from_run_id).ids;
  const toFunded = fundedForRun(to_run_id).ids;

  const ids = new Set<number>([...fromById.keys(), ...toById.keys()]);
  const initiatives: InitiativeDelta[] = [];

  for (const id of ids) {
    const f = fromById.get(id);
    const t = toById.get(id);
    let status_delta: StatusDelta;
    let rank_delta = 0;
    let score_delta = 0;

    if (f && t) {
      rank_delta = f.rank - t.rank;
      score_delta = round1(t.composite - f.composite);
      const wasFunded = fromFunded.has(id);
      const nowFunded = toFunded.has(id);
      status_delta = wasFunded && nowFunded ? 'STAYS_FUNDED'
        : !wasFunded && !nowFunded ? 'STAYS_UNFUNDED'
        : !wasFunded && nowFunded ? 'ENTERED_FUNDED'
        : 'DROPPED_BELOW';
    } else if (t && !f) {
      status_delta = 'ADDED';
    } else {
      status_delta = 'REMOVED';
    }

    initiatives.push({ use_case_id: id, title: titleFor(id), rank_delta, score_delta, status_delta });
  }

  // Biggest movers first (rank movement, then score).
  initiatives.sort((a, b) => Math.abs(b.rank_delta) - Math.abs(a.rank_delta) || b.score_delta - a.score_delta);

  const moved = initiatives.filter((i) => i.rank_delta !== 0);
  const risers = initiatives.filter((i) => i.rank_delta > 0);
  const fallers = initiatives.filter((i) => i.rank_delta < 0);
  const top_riser = risers.slice().sort((a, b) => b.rank_delta - a.rank_delta || b.score_delta - a.score_delta)[0];
  const top_faller = fallers.slice().sort((a, b) => a.rank_delta - b.rank_delta || a.score_delta - b.score_delta)[0];

  return {
    from_run_id,
    to_run_id,
    initiatives,
    summary: {
      moved: moved.length,
      risers: risers.length,
      fallers: fallers.length,
      entered_funded: initiatives.filter((i) => i.status_delta === 'ENTERED_FUNDED').length,
      dropped_below: initiatives.filter((i) => i.status_delta === 'DROPPED_BELOW').length,
      top_riser,
      top_faller,
    },
  };
}
